import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import * as game from '@/lib-backend/methods/game';
import { GameVariant, GameVariantTemplate } from '@/lib-backend';

export type CreateVariantResult =
    | { kind: 'ok'; variant: GameVariant }
    | { kind: 'error'; reason: game.CreateGameVariantResult };

export type UpdateVariantResult =
    | { kind: 'ok'; variant: GameVariant }
    | { kind: 'error'; reason: game.UpdateGameVariantResult };

@Injectable({ providedIn: 'root' })
export class RealmGameVariantsService {
    private readonly api = inject(ApiService);
    private readonly router = inject(Router);
    private readonly notification = inject(NotificationService);

    readonly variants = signal<GameVariant[]>([]);
    readonly loading = signal(false);
    readonly error = signal<string | null>(null);

    readonly templates = signal<GameVariantTemplate[]>([]);
    readonly templatesLoading = signal(false);

    private currentScope: { realmId: string; gameId: string } | null = null;

    async loadVariants(realmId: string, gameId: string): Promise<void> {
        this.currentScope = { realmId, gameId };
        this.loading.set(true);
        this.error.set(null);
        try {
            const resp = await this.api.client.call(
                game.listGameVariants({ RealmId: realmId, GameId: gameId })
            );
            if (resp.Result === 'Ok') {
                this.variants.set(resp.GameTypes);
            } else {
                this.error.set(`Failed to load variants: ${resp.Result}`);
                this.variants.set([]);
            }
        } catch (e) {
            this.error.set('Failed to load variants.');
            handleApiError(e, this.router, this.notification);
        } finally {
            this.loading.set(false);
        }
    }

    async loadTemplates(realmId: string, gameId: string): Promise<void> {
        this.templatesLoading.set(true);
        try {
            const resp = await this.api.client.call(
                game.listGameVariantTemplates({ RealmId: realmId, GameId: gameId })
            );
            if (resp.Result === 'Ok') {
                this.templates.set(resp.Templates);
            } else {
                this.templates.set([]);
            }
        } catch (e) {
            handleApiError(e, this.router, this.notification);
        } finally {
            this.templatesLoading.set(false);
        }
    }

    async createVariant(params: game.CreateGameVariantRequest): Promise<CreateVariantResult> {
        try {
            const resp = await this.api.client.call(game.createGameVariant(params));
            if (resp.Result === 'Ok') {
                this.variants.update((list) => [...list, resp.GameVariant]);
                this.notification.show({ severity: 'success', summary: 'Variant created' });
                return { kind: 'ok', variant: resp.GameVariant };
            }
            return { kind: 'error', reason: resp.Result };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error', reason: 'Unknown' };
        }
    }

    async updateVariant(params: game.UpdateGameVariantRequest): Promise<UpdateVariantResult> {
        try {
            const resp = await this.api.client.call(game.updateGameVariant(params));
            if (resp.Result === 'Ok') {
                this.variants.update((list) =>
                    list.map((v) =>
                        v.GameVariantId === resp.GameVariant.GameVariantId ? resp.GameVariant : v
                    )
                );
                this.notification.show({ severity: 'success', summary: 'Variant updated' });
                return { kind: 'ok', variant: resp.GameVariant };
            }
            return { kind: 'error', reason: resp.Result };
        } catch (e) {
            handleApiError(e, this.router, this.notification);
            return { kind: 'error', reason: 'Unknown' };
        }
    }

    clear(): void {
        this.variants.set([]);
        this.templates.set([]);
        this.error.set(null);
        this.currentScope = null;
    }

    getCurrentScope(): { realmId: string; gameId: string } | null {
        return this.currentScope;
    }
}
